if (!customElements.get('coffee-calculator')) {
  const CUP_RULES = {
    1: { options: ['500g every 4 weeks', '250g every 2 weeks'] },
    2: { options: ['1kg every 4 weeks', '500g every 2 weeks', '250g every week'] },
    3: { options: ['500g every week', '1kg + 500g every 4 weeks'] },
    4: { options: ['500g every week', '1kg every 2 weeks'] },
    5: { options: ['1kg + 500g + 250g every 3 weeks', '2kg every 3 weeks'] },
    6: { options: ['1kg + 500g every 2 weeks', '3kg every 4 weeks'] },
    7: { options: ['1kg every week', '3kg every 3 weeks'] },
    8: { options: ['1kg every week', '2kg every 2 weeks'] },
    9: { options: ['1kg + 250g every week', '4kg + 250g every 4 weeks'] },
    10: { options: ['1kg + 250g every week', '5kg every 4 weeks'] },
    11: { options: ['4kg every 3 weeks', '1kg + 500g every week'] },
    12: { options: ['1kg + 500g every week', '3kg every 2 weeks', '6kg every 4 weeks'] },
    13: { options: ['5kg every 3 weeks'] },
    14: { options: ['2kg every week', '5kg every 3 weeks', '7kg every 4 weeks'] },
    15: { options: ['2kg every week', '7kg every 4 weeks'] },
  };

  customElements.define(
    'coffee-calculator',
    class CoffeeCalculator extends HTMLElement {
      constructor() {
        super();
        this.slider = this.querySelector('.js-cups-slider');
        this.sliderFill = this.querySelector('.js-cups-slider-fill');
        this.sliderThumb = this.querySelector('.js-cups-slider-thumb');
        this.cupsValue = this.querySelector('.js-cups-value');
        this.sliderValue = this.querySelector('.js-slider-value');
        this.sliderIndicator = this.querySelector('.js-slider-indicator');
        this.bagsContainer = this.querySelector('.js-coffee-bags');

        if (!this.slider || !this.cupsValue || !this.sliderThumb || !this.sliderFill) return;

        this.min = Number(this.slider.dataset.min) || 1;
        this.max = Number(this.slider.dataset.max) || 15;
        this.value = Number(this.slider.dataset.value) || this.min;
        this.value = Math.min(this.max, Math.max(this.min, this.value));
        this.position = (this.value - this.min) / (this.max - this.min || 1);
        this.lastRecommendedValue = null;
        this.isDragging = false;

        this.updateUI = this.updateUI.bind(this);
        this.updateRecommendations = this.updateRecommendations.bind(this);
        this.setValueFromClientX = this.setValueFromClientX.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.equalizeBagTextHeights = this.equalizeBagTextHeights.bind(this);
        this.bindEvents();
        this.updateUI();
      }

      bindEvents() {
        this.slider.addEventListener('pointerdown', this.handlePointerDown);
        this.slider.addEventListener('keydown', this.handleKeydown);
        window.addEventListener('resize', this.handleResize);
      }

      getRatio() {
        return Math.min(1, Math.max(0, this.position));
      }

      updateUI() {
        const ratio = this.getRatio();
        const progress = ratio * 100;
        const rawValue = this.min + (ratio * (this.max - this.min));
        const nextValue = Math.round(rawValue);
        this.value = nextValue;
        const displayValue = String(this.value);

        this.slider.setAttribute('aria-valuenow', displayValue);
        this.slider.dataset.value = displayValue;
        this.sliderFill.style.width = `${progress}%`;
        this.sliderThumb.style.left = `${progress}%`;
        this.cupsValue.textContent = displayValue;
        if (this.sliderValue) this.sliderValue.textContent = displayValue;
        if (this.sliderIndicator) this.sliderIndicator.style.setProperty('--indicator-left', `${progress}%`);
        if (this.lastRecommendedValue !== this.value) {
          this.updateRecommendations(this.value);
          this.lastRecommendedValue = this.value;
        }
      }

      getBagForOption(option) {
        const normalized = option.trim().toLowerCase();
        if (normalized.includes('+')) return 'combined';
        if (/^\d+(?:\.\d+)?\s*kg\b/.test(normalized)) return 'large';
        if (/^\d+(?:\.\d+)?\s*g\b/.test(normalized)) {
          if (normalized.startsWith('500g')) return 'medium';
          if (normalized.startsWith('250g')) return 'small';
        }
        if (/\bkg\b/.test(normalized)) return 'large';
        if (/\b500g\b/.test(normalized)) return 'medium';
        if (/\b250g\b/.test(normalized)) return 'small';
        return null;
      }

      escapeHtml(text) {
        return String(text)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }

      escapeAttr(text) {
        return String(text)
          .replace(/&/g, '&amp;')
          .replace(/"/g, '&quot;');
      }

      getBagAssets(type) {
        const map = {
          combined: {
            src: this.getAttribute('data-img-combined'),
            alt: 'Combined coffee bag',
            w: 82,
            h: 173,
          },
          large: {
            src: this.getAttribute('data-img-large'),
            alt: 'Large coffee bag',
            w: 82,
            h: 173,
          },
          medium: {
            src: this.getAttribute('data-img-medium'),
            alt: 'Medium coffee bag',
            w: 79,
            h: 126,
          },
          small: {
            src: this.getAttribute('data-img-small'),
            alt: 'Small coffee bag',
            w: 70,
            h: 111,
          },
        };
        return map[type];
      }

      renderBagHtml(item) {
        const assets = this.getBagAssets(item.type);
        if (!assets || !assets.src) return '';
        const { src, alt, w, h } = assets;
        return `<div class="coffee-calculator__bag" data-bag-type="${item.type}"><img src="${this.escapeAttr(src)}" alt="${this.escapeAttr(alt)}" width="${w}" height="${h}" loading="lazy"><div class="coffee-calculator__bag-options"><p>${this.escapeHtml(item.text)}</p></div></div>`;
      }

      updateRecommendations(cups) {
        const rules = CUP_RULES[cups] || CUP_RULES[1];
        const items = rules.options
          .map((text) => {
            const type = this.getBagForOption(text);
            return type ? { type, text } : null;
          })
          .filter(Boolean);

        if (!this.bagsContainer) return;

        if (!items.length) {
          this.bagsContainer.innerHTML = '';
          this.bagsContainer.classList.remove('coffee-calculator__bags--few-visible');
          return;
        }

        const html = items
          .map((item, index) => {
            const sep = index > 0
              ? '<div class="coffee-calculator__bag-separator" aria-hidden="true">OR</div>'
              : '';
            return sep + this.renderBagHtml(item);
          })
          .join('');

        this.bagsContainer.innerHTML = html;
        this.bagsContainer.classList.toggle('coffee-calculator__bags--few-visible', items.length <= 2);
        this.equalizeBagTextHeights();
      }

      equalizeBagTextHeights() {
        const optionBlocks = this.querySelectorAll('.js-coffee-bags .coffee-calculator__bag-options');
        if (!optionBlocks.length) return;

        optionBlocks.forEach((block) => {
          block.style.minHeight = '0px';
        });

        let tallest = 0;
        optionBlocks.forEach((block) => {
          tallest = Math.max(tallest, block.offsetHeight);
        });

        optionBlocks.forEach((block) => {
          block.style.minHeight = `${tallest}px`;
        });
      }

      setValueFromClientX(clientX) {
        const rect = this.slider.getBoundingClientRect();
        if (!rect.width) return;
        const clampedX = Math.min(rect.right, Math.max(rect.left, clientX));
        const ratio = (clampedX - rect.left) / rect.width;
        this.position = Math.min(1, Math.max(0, ratio));
        this.updateUI();
      }

      handlePointerDown(e) {
        e.preventDefault();
        this.slider.focus();
        this.isDragging = true;
        this.setValueFromClientX(e.clientX);
        window.addEventListener('pointermove', this.handlePointerMove);
        window.addEventListener('pointerup', this.handlePointerUp);
      }

      handlePointerMove(e) {
        if (!this.isDragging) return;
        this.setValueFromClientX(e.clientX);
      }

      handlePointerUp() {
        this.isDragging = false;
        window.removeEventListener('pointermove', this.handlePointerMove);
        window.removeEventListener('pointerup', this.handlePointerUp);
      }

      handleKeydown(e) {
        const key = e.key;
        if (!['ArrowLeft', 'ArrowDown', 'ArrowRight', 'ArrowUp', 'Home', 'End'].includes(key)) return;
        e.preventDefault();

        if (key === 'Home') this.value = this.min;
        if (key === 'End') this.value = this.max;
        if (key === 'ArrowLeft' || key === 'ArrowDown') this.value -= 1;
        if (key === 'ArrowRight' || key === 'ArrowUp') this.value += 1;

        this.value = Math.min(this.max, Math.max(this.min, this.value));
        this.position = (this.value - this.min) / (this.max - this.min || 1);
        this.updateUI();
      }

      handleResize() {
        this.updateUI();
        this.equalizeBagTextHeights();
      }
    },
  );
}
